import { useState } from 'react';
import { MEMORY_CATEGORIES, type MemoryCategory } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section } from '../../components/common';

/** Strip SDK prefixes so a failure reads as a sentence, not a stack trace. */
const reasonOf = (e: unknown) => String(e).replace(/^(FirebaseError|Error):\s*/, '').slice(0, 200);

/** "What Ally knows about me" — full inspect/edit/delete control. */
export function MemoryScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [adding, setAdding] = useState(false);
  const [newCategory, setNewCategory] = useState<MemoryCategory>('interest');
  const [newContent, setNewContent] = useState('');
  // Feedback is keyed to the card that caused it — a single banner at the top
  // of a long list is easy to miss when the failing button is far below it.
  const [error, setError] = useState<{ id: string; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const suggested = app.memories.filter((m) => m.status === 'suggested');
  const confirmed = app.memories.filter((m) => m.status === 'confirmed');

  const act = async (id: string, action: 'confirm' | 'reject' | 'delete' | 'edit', content?: string) => {
    setBusyId(id);
    setError(null);
    try {
      await app.backend.resolveMemory(id, action, content, action === 'confirm' ? true : undefined);
      await app.refreshMemories();
      // Leave edit mode only once it actually landed, so a failure keeps the
      // rewritten text instead of silently discarding it.
      setEditing(null);
    } catch (e) {
      setError({ id, text: `Could not update that memory. ${reasonOf(e)}` });
    } finally {
      setBusyId(null);
    }
  };

  const addManual = async () => {
    const content = newContent.trim();
    // Save looked live but did nothing at all on an empty box — say why.
    if (!content) {
      setError({ id: 'add', text: 'Write the memory first — there is nothing to save yet.' });
      return;
    }
    setBusyId('add');
    setError(null);
    const now = new Date().toISOString();
    try {
      await app.backend.addMemory({
        id: `manual-${Date.now()}`,
        category: newCategory,
        content,
        status: 'confirmed',
        source: 'user-stated',
        confidence: 1,
        sensitive: false,
        cloudApproved: true,
        createdAtISO: now,
        updatedAtISO: now,
      });
      setNewContent('');
      setAdding(false);
      await app.refreshMemories();
    } catch (e) {
      setError({ id: 'add', text: `Could not save that memory. ${reasonOf(e)}` });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('memory.title')} back />

      {app.memories.length === 0 && (
        <div className="card muted small" style={{ lineHeight: 1.6 }}>{t('memory.empty')}</div>
      )}

      {suggested.length > 0 && (
        <Section title={t('memory.suggested')}>
          <div className="col">
            {suggested.map((m) => (
              <div key={m.id} className="card card--strong fade-in">
                <div className="tiny faint" style={{ marginBottom: 4 }}>
                  {m.category.replace(/-/g, ' ')} · confidence {(m.confidence * 100).toFixed(0)}%
                  {m.sensitive && <span className="danger"> · {t('memory.sensitive')}</span>}
                </div>
                <div className="small" style={{ marginBottom: 10 }}>{m.content}</div>
                <div className="row">
                  <button
                    className="btn btn--primary btn--small"
                    disabled={busyId === m.id}
                    onClick={() => act(m.id, 'confirm')}
                  >
                    {busyId === m.id ? <span className="spinner" /> : t('memory.confirm')}
                  </button>
                  <button
                    className="btn btn--ghost btn--small"
                    disabled={busyId === m.id}
                    onClick={() => act(m.id, 'reject')}
                  >
                    {t('memory.reject')}
                  </button>
                </div>
                {error?.id === m.id && (
                  <div className="banner banner--danger small" role="alert" style={{ marginTop: 8 }}>
                    {error.text}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {confirmed.length > 0 && (
        <Section title={t('memory.confirmed')}>
          <div className="col">
            {confirmed.map((m) => (
              <div key={m.id} className="card">
                <div className="tiny faint" style={{ marginBottom: 4 }}>
                  {m.category.replace(/-/g, ' ')} · {m.source}
                </div>
                {editing === m.id ? (
                  <div className="col">
                    <textarea
                      className="input"
                      rows={2}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className="row">
                      <button
                        className="btn btn--primary btn--small"
                        disabled={busyId === m.id}
                        onClick={() => act(m.id, 'edit', editText)}
                      >
                        {busyId === m.id ? <span className="spinner" /> : t('common.save')}
                      </button>
                      <button className="btn btn--ghost btn--small" onClick={() => setEditing(null)}>
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="small" style={{ marginBottom: 8 }}>{m.content}</div>
                    <div className="row">
                      <button
                        className="chip"
                        onClick={() => {
                          setError(null);
                          setEditing(m.id);
                          setEditText(m.content);
                        }}
                      >
                        {t('memory.edit')}
                      </button>
                      <button
                        className="chip"
                        style={{ color: 'var(--danger)' }}
                        disabled={busyId === m.id}
                        onClick={() => act(m.id, 'delete')}
                      >
                        {t('memory.delete')}
                      </button>
                    </div>
                  </>
                )}
                {error?.id === m.id && (
                  <div className="banner banner--danger small" role="alert" style={{ marginTop: 8 }}>
                    {error.text}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="divider" />
      {adding ? (
        <div className="card col">
          <select className="select" value={newCategory} onChange={(e) => setNewCategory(e.target.value as MemoryCategory)}>
            {MEMORY_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.replace(/-/g, ' ')}</option>
            ))}
          </select>
          <textarea
            className="input"
            rows={2}
            placeholder="e.g. I prefer voice notes over long texts"
            value={newContent}
            onChange={(e) => {
              setError(null);
              setNewContent(e.target.value);
            }}
          />
          {error?.id === 'add' && (
            <div className="banner banner--danger small" role="alert">{error.text}</div>
          )}
          <div className="row">
            <button
              className={`btn btn--primary btn--small ${newContent.trim() ? '' : 'btn--incomplete'}`}
              disabled={busyId === 'add'}
              onClick={addManual}
            >
              {busyId === 'add' ? <span className="spinner" /> : t('common.save')}
            </button>
            <button
              className="btn btn--ghost btn--small"
              onClick={() => {
                setError(null);
                setAdding(false);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="btn btn--ghost btn--block"
          onClick={() => {
            setError(null);
            setAdding(true);
          }}
        >
          + {t('memory.add')}
        </button>
      )}
    </div>
  );
}
